<template>
  <div
    class="cb"
    :class="{ 'cb--mine': mine, 'cb--tail': tail }"
    :data-mid="message.id"
  >
    <div
      class="cb__bubble"
      :class="{ 'cb__bubble--media': isMedia, 'cb__bubble--failed': message.status === 'failed' }"
    >
      <span
        v-if="message.forwarded"
        class="cb__forwarded"
      ><feather-icon
        icon="CornerUpRightIcon"
        size="12"
      /> {{ $t('chat.forwarded') }}</span>

      <button
        v-if="message.reply_to"
        type="button"
        class="cb__quote"
        @click="$emit('jump', message.reply_to.id)"
      >
        <strong>{{ quoteAuthor }}</strong>
        <span>{{ quoteText }}</span>
      </button>

      <!-- Imagem: reserva o espaço pela proporção real, então a lista não "pula" quando a imagem carrega -->
      <button
        v-if="message.kind === 'image' && message.attachment"
        type="button"
        class="cb__image"
        :style="imageBox"
        :aria-label="$t('chat.openImage')"
        @click="$emit('open-image', message)"
      >
        <img
          :src="message.attachment.url"
          :alt="message.attachment.name"
          loading="lazy"
          decoding="async"
        >
        <span
          v-if="message.status === 'sending'"
          class="cb__progress"
        >{{ message.progress }}%</span>
      </button>

      <chat-audio-player
        v-else-if="message.kind === 'audio' && message.attachment"
        :src="message.attachment.url"
        :duration="message.attachment.duration || 0"
      />

      <a
        v-else-if="message.kind === 'file' && message.attachment"
        class="cb__file"
        :href="message.status === 'sending' ? undefined : message.attachment.url"
        target="_blank"
        rel="noopener"
        :download="message.attachment.name"
      >
        <span class="cb__file-icon"><feather-icon
          :icon="fileIcon"
          size="20"
        /></span>
        <span class="cb__file-info">
          <strong :title="message.attachment.name">{{ message.attachment.name }}</strong>
          <small>
            {{ size(message.attachment.size) }}
            <template v-if="message.status === 'sending'"> · {{ message.progress }}%</template>
          </small>
        </span>
      </a>

      <p
        v-if="message.body"
        class="cb__text"
      >{{ message.body }}</p>

      <span class="cb__foot">
        <span
          v-if="message.edited_at"
          class="cb__edited"
        >{{ $t('chat.edited') }}</span>
        <span class="cb__time">{{ time }}</span>
        <template v-if="mine">
          <feather-icon
            v-if="message.status === 'sending'"
            icon="ClockIcon"
            size="13"
            class="cb__tick"
          />
          <feather-icon
            v-else-if="message.status === 'failed'"
            icon="AlertCircleIcon"
            size="13"
            class="cb__tick cb__tick--failed"
          />
          <svg
            v-else
            class="cb__tick"
            :class="{ 'cb__tick--read': read }"
            width="17"
            height="13"
            viewBox="0 0 17 13"
            fill="none"
            stroke="currentColor"
            stroke-width="1.8"
            stroke-linecap="round"
            stroke-linejoin="round"
            role="img"
            :aria-label="tickLabel"
          >
            <path d="M1 7l3.5 3.5L11 3" />
            <path
              v-if="delivered"
              d="M7 10.5L9 12.5 15.5 3"
            />
          </svg>
        </template>
        <b-dropdown
          v-if="actionable"
          class="cb__menu"
          variant="link"
          no-caret
          lazy
          boundary="viewport"
          :right="mine"
          toggle-class="cb__menu-btn"
          menu-class="cb__menu-panel"
          :toggle-attrs="{ 'aria-label': $t('chat.menu.open') }"
        >
          <template #button-content>
            <feather-icon
              icon="ChevronDownIcon"
              size="16"
            />
          </template>
          <b-dropdown-item @click="$emit('reply', message)">
            <feather-icon
              icon="CornerUpLeftIcon"
              size="14"
              class="cb__menu-icon"
            />{{ $t('chat.menu.reply') }}
          </b-dropdown-item>
          <b-dropdown-item @click="$emit('forward', message)">
            <feather-icon
              icon="CornerUpRightIcon"
              size="14"
              class="cb__menu-icon"
            />{{ $t('chat.menu.forward') }}
          </b-dropdown-item>
          <b-dropdown-item
            v-if="message.body"
            @click="$emit('copy', message)"
          >
            <feather-icon
              icon="CopyIcon"
              size="14"
              class="cb__menu-icon"
            />{{ $t('chat.menu.copy') }}
          </b-dropdown-item>
          <b-dropdown-item
            v-if="mine"
            @click="$emit('info', message)"
          >
            <feather-icon
              icon="InfoIcon"
              size="14"
              class="cb__menu-icon"
            />{{ $t('chat.menu.info') }}
          </b-dropdown-item>
          <b-dropdown-item
            v-if="editable"
            @click="$emit('edit', message)"
          >
            <feather-icon
              icon="Edit2Icon"
              size="14"
              class="cb__menu-icon"
            />{{ $t('chat.menu.edit') }}
          </b-dropdown-item>
        </b-dropdown>
      </span>
    </div>

    <div
      v-if="message.status === 'failed'"
      class="cb__failed"
    >
      <span>{{ message.error || $t('chat.sendFailed') }}</span>
      <button
        type="button"
        @click="$emit('retry', message)"
      >
        {{ $t('chat.retry') }}
      </button>
      <button
        type="button"
        @click="$emit('discard', message)"
      >
        {{ $t('chat.discard') }}
      </button>
    </div>
  </div>
</template>

<script>
import {
  formatBytes, timeLabel, messagePreviewKey, EDIT_WINDOW_MS,
} from '@/utils/chat'
import ChatAudioPlayer from './ChatAudioPlayer.vue'

const MAX_IMAGE_W = 300
const MAX_IMAGE_H = 360

export default {
  name: 'ChatMessageBubble',

  components: { ChatAudioPlayer },

  props: {
    message: { type: Object, required: true },
    mine: { type: Boolean, default: false },
    // Primeira da sequência do mesmo remetente: ganha o "rabinho" e mais espaço acima.
    tail: { type: Boolean, default: false },
    peerReadId: { type: Number, default: 0 },
    peerDeliveredId: { type: Number, default: 0 },
    peerName: { type: String, default: '' },
    // Modo de seleção (para encaminhar várias): sem o menu de opções, e a bolha não responde a cliques próprios.
    selecting: { type: Boolean, default: false },
  },

  computed: {
    // Mensagem ainda sem id (enviando ou falha) não tem o que responder, encaminhar ou editar.
    actionable() {
      return !!this.message.id && !this.selecting
    },
    editable() {
      return this.mine && !!this.message.body && Date.now() - new Date(this.message.created_at).getTime() < EDIT_WINDOW_MS
    },
    quoteAuthor() {
      return this.message.reply_to.sender_id === this.$store.state.user.id ? this.$t('chat.you') : this.peerName
    },
    quoteText() {
      const reply = this.message.reply_to
      const key = messagePreviewKey(reply)

      return reply.body || (key ? this.$t(key) : reply.attachment_name) || ''
    },
    isMedia() {
      return this.message.kind === 'image' && !this.message.body
    },
    read() {
      return !!this.message.id && this.message.id <= this.peerReadId
    },
    // Já chegou ao aparelho do outro lado (✓✓); sem isso fica no ✓ simples, como no WhatsApp quando ele está sem internet.
    delivered() {
      return this.read || (!!this.message.id && this.message.id <= this.peerDeliveredId)
    },
    tickLabel() {
      if (this.read) return this.$t('chat.read')

      return this.delivered ? this.$t('chat.delivered') : this.$t('chat.sent')
    },
    time() {
      return timeLabel(this.message.created_at, this.$i18n.locale)
    },
    fileIcon() {
      const mime = (this.message.attachment && this.message.attachment.mime) || ''
      if (mime === 'application/pdf') return 'FileTextIcon'
      if (mime.startsWith('video/')) return 'VideoIcon'

      return 'FileIcon'
    },
    imageBox() {
      const a = this.message.attachment
      if (!a || !a.width || !a.height) return { width: '240px', height: '180px' }
      const scale = Math.min(1, MAX_IMAGE_W / a.width, MAX_IMAGE_H / a.height)

      return { width: `${Math.round(a.width * scale)}px`, height: `${Math.round(a.height * scale)}px` }
    },
  },

  methods: {
    size: formatBytes,
  },
}
</script>

<style scoped>
.cb {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin-top: 2px;
}

.cb--tail { margin-top: 10px; }
.cb--mine { align-items: flex-end; }

.cb__bubble {
  position: relative;
  max-width: min(78%, 520px);
  padding: 7px 10px 5px;
  border-radius: 10px;
  background: var(--chat-in);
  box-shadow: 0 1px 1px rgba(34, 41, 47, 0.12);
  color: var(--chat-text);
  overflow-wrap: anywhere;
}

.cb--mine .cb__bubble { background: var(--chat-out); }

/* "Rabinho" só na primeira bolha da sequência, no canto de quem fala. */
.cb--tail .cb__bubble { border-top-left-radius: 2px; }
.cb--tail.cb--mine .cb__bubble { border-top-left-radius: 10px; border-top-right-radius: 2px; }

.cb__bubble--media { padding: 3px; }
.cb__bubble--failed { outline: 1px solid rgba(234, 84, 85, 0.55); }

/* Seta de opções no rodapé, ao lado da hora (como no WhatsApp): só aparece com o mouse sobre a bolha e
   não ocupa espaço enquanto escondida, então o texto não ganha um "balão" por cima. */
.cb__menu { display: inline-flex; }

.cb__menu ::v-deep .cb__menu-btn {
  display: inline-flex;
  max-width: 0;
  padding: 0;
  border: 0;
  overflow: hidden;
  background: transparent;
  color: inherit;
  line-height: 1;
  opacity: 0;
  transition: max-width 0.12s, opacity 0.12s;
}

.cb__bubble:hover .cb__menu ::v-deep .cb__menu-btn,
.cb__menu.show ::v-deep .cb__menu-btn,
.cb__menu ::v-deep .cb__menu-btn:focus-visible {
  max-width: 20px;
  opacity: 1;
}

/* Painel do menu: cartão limpo, itens com ícone à esquerda (padrão WhatsApp). */
.cb__menu ::v-deep .cb__menu-panel {
  min-width: 190px;
  padding: 6px 0;
  border: 0;
  border-radius: 12px;
  background: var(--chat-menu-panel, #fff);
  box-shadow: 0 8px 28px rgba(34, 41, 47, 0.22);
  font-size: 0.9rem;
}

.dark-layout .cb { --chat-menu-panel: #283046; }

.cb__menu ::v-deep .cb__menu-panel .dropdown-item {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 9px 20px;
  color: var(--chat-text);
  font-size: 0.9rem;
}

.cb__menu ::v-deep .cb__menu-panel .dropdown-item:hover,
.cb__menu ::v-deep .cb__menu-panel .dropdown-item:focus {
  background: rgba(127, 127, 127, 0.14);
  color: var(--chat-text);
}

.cb__menu-icon { flex: none; opacity: 0.75; }

/* Sem mouse não há hover: a seta fica sempre à vista. */
@media (hover: none) {
  .cb__menu ::v-deep .cb__menu-btn { max-width: 20px; opacity: 0.8; }
}

.cb__forwarded {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-bottom: 2px;
  color: var(--chat-muted);
  font-size: 0.72rem;
  font-style: italic;
}

.cb__quote {
  display: flex;
  flex-direction: column;
  width: 100%;
  min-width: 0;
  margin-bottom: 4px;
  padding: 4px 8px;
  border: 0;
  border-left: 3px solid #7367f0;
  border-radius: 6px;
  background: rgba(115, 103, 240, 0.12);
  color: inherit;
  text-align: left;
}

.cb__quote strong { color: #7367f0; font-size: 0.78rem; }

.cb__quote span {
  display: -webkit-box;
  overflow: hidden;
  font-size: 0.82rem;
  opacity: 0.85;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.cb__edited { font-style: italic; }

/* Destaque ao pular para a mensagem citada. */
.cb--flash .cb__bubble { animation: cb-flash 1.4s ease; }

@keyframes cb-flash { 30% { box-shadow: 0 0 0 3px rgba(115, 103, 240, 0.55); } }

.cb__text {
  margin: 0;
  font-size: 0.93rem;
  line-height: 1.38;
  white-space: pre-wrap;
}

.cb__image {
  position: relative;
  display: block;
  padding: 0;
  border: 0;
  border-radius: 8px;
  background: rgba(127, 127, 127, 0.15);
  overflow: hidden;
  cursor: zoom-in;
}

.cb__image img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.cb__progress {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.35);
  color: #fff;
  font-weight: 700;
}

.cb__file {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 220px;
  max-width: 300px;
  margin-bottom: 2px;
  padding: 8px;
  border-radius: 8px;
  background: rgba(127, 127, 127, 0.12);
  color: inherit;
  text-decoration: none;
}

.cb__file:hover { background: rgba(127, 127, 127, 0.2); color: inherit; }

.cb__file-icon {
  display: inline-flex;
  flex: none;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  border-radius: 8px;
  background: rgba(115, 103, 240, 0.18);
  color: #7367f0;
}

.cb__file-info { display: flex; flex-direction: column; min-width: 0; }
.cb__file-info strong { overflow: hidden; font-size: 0.86rem; text-overflow: ellipsis; white-space: nowrap; }
.cb__file-info small { opacity: 0.7; }

.cb__foot {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 4px;
  margin-top: 1px;
  font-size: 0.68rem;
}

.cb__time,
.cb__edited,
.cb__tick:not(.cb__tick--read):not(.cb__tick--failed) { opacity: 0.7; }

.cb__bubble--media .cb__foot {
  position: absolute;
  right: 8px;
  bottom: 7px;
  padding: 1px 6px;
  border-radius: 10px;
  background: rgba(0, 0, 0, 0.45);
  color: #fff;
}

.cb__time { font-variant-numeric: tabular-nums; }
.cb__tick { flex: none; }
.cb__tick--read { color: #34b7f1; }
.cb__tick--failed { color: #ea5455; }

.cb__failed {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 3px;
  color: #ea5455;
  font-size: 0.75rem;
}

.cb__failed button {
  padding: 0;
  border: 0;
  background: none;
  color: inherit;
  font-weight: 700;
  text-decoration: underline;
}

@media (max-width: 767px) {
  .cb__bubble { max-width: 88%; }
}
</style>
